/**
 * Puck-zones ⇄ owned-nested-tree adapter (R4 / content-spine zones cutover).
 *
 * The STORED page shape is an owned nested tree: a `Columns` node carries its
 * children inline under `props.columns` (`columns[i]` = the child list of the
 * i-th column). Puck's editor, by contrast, speaks a flat `Data.zones` map
 * keyed `<componentId>:col-<i>`. This module is the SINGLE place that knows the
 * `<id>:col-<i>` convention; it bridges the two at exactly the two Puck editor
 * boundaries:
 *
 *   - editor LOAD  → `toPuckZones(stored)`  : nested tree → Puck `Data` (+zones)
 *   - editor SAVE  → `fromPuckZones(data)`  : Puck `Data` (+zones) → nested tree
 *
 * There is no web `<Render>`; mobile and the backend validator consume the
 * owned tree directly, so these are the only zone-aware seams.
 *
 * Where the node id lives
 * -----------------------
 * Puck keeps a component's id at `props.id`, and keys its zones
 * `<props.id>:col-<i>`. This module reads it through `resolveNodeId` rather
 * than a top-level `node.id` — the latter is `undefined` for every real editor
 * payload, which made both directions match zero zone keys and silently store
 * empty columns (every block an author dropped into a column was discarded on
 * the next autosave).
 *
 * Canonical empty-column representation
 * -------------------------------------
 * Puck only creates a `zones` entry for a DropZone that has at least one child
 * — an empty column produces NO key. To reproduce Puck's exact map,
 * `toPuckZones` therefore OMITS empty columns from the zones map (it does not
 * emit `<id>:col-<i>: []`). The owned tree, by contrast, stores a DENSE
 * `columns` array of length `count` with `[]` for empty columns, so renderers
 * can index `columns[0..count-1]` without holes. `fromPuckZones` rebuilds that
 * dense array, reading each missing/empty key as `[]`.
 *
 * Consequences (both directions are pure and mutually inverse up to this
 * normalization):
 *   - `fromPuckZones(toPuckZones(nested))` === `nested` (dense columns preserved)
 *   - `toPuckZones(fromPuckZones(realPuckSave))` reproduces the original Puck
 *     zones map, because a real Puck save already omits empty-column keys. A
 *     save that carried an explicit `<id>:col-<i>: []` normalizes to "absent",
 *     which Puck treats identically on load (both render an empty column).
 */

import { resolveNodeId, type ContentPage } from './page'

const COLUMN_COUNT_DEFAULT = 2

const zoneKey = (id: string, columnIndex: number): string => `${id}:col-${columnIndex}`

interface AdapterNode {
  type?: unknown
  id?: unknown
  props?: Record<string, unknown>
  [key: string]: unknown
}

type ZonesMap = Record<string, AdapterNode[]>

/** Puck-shaped page data — `content` plus a flat `zones` map. */
export interface PuckZonesData {
  content: AdapterNode[]
  zones: ZonesMap
  [key: string]: unknown
}

function isColumns(node: AdapterNode): boolean {
  return !!node && typeof node === 'object' && node.type === 'Columns'
}

function columnCount(node: AdapterNode): number {
  return node.props?.count === 3 ? 3 : COLUMN_COUNT_DEFAULT
}

/**
 * Highest column index present in `zones` for a given Columns id, as a length
 * (max index + 1). Used so the adapter never truncates overflow children when a
 * Columns `count` was reduced below the number of already-filled columns
 * (review #3): we iterate `max(count, presentLength)` and preserve the parked
 * columns rather than silently dropping `columns[i]` for `i >= count`.
 */
function presentColumnLength(id: string | undefined, zones: ZonesMap): number {
  if (!id) return 0
  const prefix = `${id}:col-`
  let length = 0
  for (const key of Object.keys(zones)) {
    if (!key.startsWith(prefix)) continue
    const idx = Number(key.slice(prefix.length))
    if (Number.isInteger(idx) && idx >= 0 && idx + 1 > length) length = idx + 1
  }
  return length
}

// ── Puck zones → owned nested tree ──────────────────────────────────────────

function liftNode(node: AdapterNode, zones: ZonesMap): AdapterNode {
  if (!isColumns(node)) return node
  const id = resolveNodeId(node)
  const count = columnCount(node)
  // Preserve overflow: if a zone key exists for a column beyond `count` (the
  // author reduced count after filling that column), keep it instead of
  // dropping the children. Renderers still lay out only `count` columns; the
  // parked data round-trips losslessly and reappears if count is restored.
  const length = Math.max(count, presentColumnLength(id, zones))
  const columns: AdapterNode[][] = []
  for (let i = 0; i < length; i++) {
    const raw = id ? zones[zoneKey(id, i)] : undefined
    const children = Array.isArray(raw) ? raw : []
    columns.push(children.map(child => liftNode(child, zones)))
  }
  // Drop any nested `zones` map carried on this node's props (review #7): post
  // cutover children live under `props.columns`, so a leftover `props.zones`
  // would be stale bloat that survives `.passthrough()`. Strip it here as well
  // as rejecting it at the schema boundary.
  const props = { ...(node.props ?? {}), columns }
  delete (props as { zones?: unknown }).zones
  return { ...node, props }
}

/**
 * Convert a Puck `Data` payload (with a flat `zones` map) into the owned nested
 * tree. The returned value has no `zones` key; every `Columns` node carries a
 * dense `props.columns` array of length `count`.
 */
export function fromPuckZones(data: unknown): ContentPage {
  const source = (data && typeof data === 'object' ? data : {}) as {
    content?: unknown
    zones?: unknown
    [key: string]: unknown
  }
  const zones: ZonesMap = source.zones && typeof source.zones === 'object' ? (source.zones as ZonesMap) : {}
  const content = Array.isArray(source.content) ? (source.content as AdapterNode[]) : []
  const lifted = content.map(node => liftNode(node, zones))
  const rest = { ...source }
  delete rest.zones
  return { ...rest, content: lifted } as unknown as ContentPage
}

// ── Owned nested tree → Puck zones ──────────────────────────────────────────

function flattenNode(node: AdapterNode, zones: ZonesMap): AdapterNode {
  if (!isColumns(node)) return node
  const id = resolveNodeId(node)
  const count = columnCount(node)
  const owned = Array.isArray(node.props?.columns) ? (node.props!.columns as AdapterNode[][]) : []
  // Emit overflow columns too (review #3): if the owned tree carries more than
  // `count` filled columns, hoist them into `zones` so a subsequent
  // `fromPuckZones` round-trip restores them rather than truncating at `count`.
  const length = Math.max(count, owned.length)
  for (let i = 0; i < length; i++) {
    const children = Array.isArray(owned[i]) ? owned[i] : []
    // Omit empty columns to reproduce Puck's exact map (see header note).
    if (id && children.length > 0) {
      zones[zoneKey(id, i)] = children.map(child => flattenNode(child, zones))
    }
  }
  const props = { ...(node.props ?? {}) }
  delete props.columns
  return { ...node, props }
}

/**
 * Convert the owned nested tree into a Puck `Data` payload with a flat `zones`
 * map. The `props.columns` field is stripped from every `Columns` node; its
 * children are hoisted into `zones` keyed `<id>:col-<i>` (empty columns
 * omitted).
 */
export function toPuckZones(page: unknown): PuckZonesData {
  const source = (page && typeof page === 'object' ? page : {}) as {
    content?: unknown
    [key: string]: unknown
  }
  const zones: ZonesMap = {}
  const content = Array.isArray(source.content) ? (source.content as AdapterNode[]) : []
  const flattened = content.map(node => flattenNode(node, zones))
  const rest = { ...source }
  delete rest.zones
  return { ...rest, content: flattened, zones }
}
